"use client";

import { useQuery } from "@tanstack/react-query";

import { type ReactNode, useState } from "react";
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiCheck,
  FiChevronDown,
  FiChevronRight,
  FiClock,
} from "react-icons/fi";

import Link from "next/link";

import { GFEExpiringSoonBadge } from "@/components/patient/gfe-validity";
import CreateNoteWrapper from "@/components/scribe/create-note-wrapper";
import { GFEOutcomeGroups } from "@/components/spakinect/gfe-outcome-groups";
import Badge from "@/components/ui/badge";
import Button from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { Skeleton } from "@/components/ui/skeleton";

import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";

import { formatDate } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { clickableText, cx } from "@/lib/tailwind-utils";

import { NoteType } from "@/types/enums/note-state";
import {
  GFEExamStatus,
  GFEItemOutcome,
  GFESource,
  type PatientGFEDetail,
} from "@/types/gfe";
import type { PatientDetail } from "@/types/patient";

import { gfeQueryOptions } from "@/query/gfe";

const STATUS_BADGE: Record<
  GFEExamStatus,
  { label: string; variant: "success" | "warning" | "error" | "neutral" }
> = {
  [GFEExamStatus.REQUESTED]: { label: "In progress", variant: "warning" },
  [GFEExamStatus.IN_PROGRESS]: { label: "In progress", variant: "warning" },
  [GFEExamStatus.COMPLETED]: { label: "Completed", variant: "success" },
  [GFEExamStatus.REJECTED]: { label: "Not cleared", variant: "error" },
  [GFEExamStatus.NOT_COMPLETED]: { label: "Not completed", variant: "neutral" },
  [GFEExamStatus.CANCELLED]: { label: "Withdrawn", variant: "neutral" },
};

// The provider's conditions or reason for deferring ride with the treatment, so the
// front desk can see why when the patient calls.
function labelsFor(gfe: PatientGFEDetail, outcome: GFEItemOutcome) {
  return gfe.items
    .filter((item) => item.outcome === outcome)
    .map((item) =>
      item.conditionNote ? `${item.label} (${item.conditionNote})` : item.label,
    );
}

function isValidClearance(gfe: PatientGFEDetail) {
  return gfe.examStatus === GFEExamStatus.COMPLETED && !gfe.isExpired;
}

/** What an exam that is still in progress waits on, so staff know who acts next. */
function waitingOnLabel(gfe: PatientGFEDetail) {
  switch (gfe.waitingOn) {
    case "UNMARKED_TREATMENTS": {
      const unmarked = gfe.items.filter(
        (item) => item.outcome === GFEItemOutcome.PENDING,
      ).length;
      return unmarked > 0
        ? `${unmarked} ${unmarked === 1 ? "treatment" : "treatments"} not marked`
        : "No treatments added";
    }
    case "SIGN_OFF":
      return "Waiting for sign-off";
    case "CO_SIGN":
      return "Waiting for co-sign";
    default:
      return null;
  }
}

function InHouseGFERow({ gfe }: { gfe: PatientGFEDetail }) {
  const { tenant } = useTenant();
  const badge = STATUS_BADGE[gfe.examStatus];
  const finished = gfe.examStatus === GFEExamStatus.COMPLETED;
  const signed = finished || gfe.examStatus === GFEExamStatus.REJECTED;
  const waitingOn = waitingOnLabel(gfe);
  return (
    <li className="space-y-1 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={gfe.isExpired && finished ? "neutral" : badge.variant}>
          {gfe.isExpired && finished ? "Expired" : badge.label}
        </Badge>
        {finished && gfe.validUntil && !gfe.isExpired && (
          <GFEExpiringSoonBadge
            validUntil={gfe.validUntil}
            isExpired={gfe.isExpired}
          />
        )}
        {gfe.noteId && tenant?.subDomain && (
          <Link
            href={`/${tenant.subDomain}/scribe/playground/${gfe.noteId}`}
            className={cx("text-caption", clickableText)}
          >
            View note
          </Link>
        )}
      </div>
      <p className="text-caption text-foreground-secondary">
        {[
          signed && gfe.examDate
            ? `Signed ${formatDate(gfe.examDate)}${
                gfe.examiningProviderName
                  ? ` by ${gfe.examiningProviderName}`
                  : ""
              }`
            : `Started ${formatDate(gfe.createdDate)}`,
          // The badge already says "Expired", so the date alone is enough.
          finished && gfe.validUntil
            ? `Valid until ${formatDate(gfe.validUntil)}`
            : null,
          waitingOn,
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>
      {gfe.items.length > 0 &&
        (gfe.examStatus === GFEExamStatus.COMPLETED ||
        gfe.examStatus === GFEExamStatus.REJECTED ? (
          <GFEOutcomeGroups
            approvedTreatments={labelsFor(gfe, GFEItemOutcome.APPROVED)}
            conditionallyApprovedTreatments={labelsFor(
              gfe,
              GFEItemOutcome.CONDITIONAL,
            )}
            deferredTreatments={labelsFor(gfe, GFEItemOutcome.DEFERRED)}
          />
        ) : (
          <p className="text-caption text-foreground-muted">
            {gfe.items.map((item) => item.label).join(", ")}
          </p>
        ))}
    </li>
  );
}

interface InHouseGFESectionProps {
  patientId: string;
  patient?: PatientDetail | null;
  /** The appointment the GFE is for, when started from its checklist. */
  eventId?: string | null;
  locationId?: string;
  showHeading?: boolean;
  className?: string;
  /**
   * SpaKinect / Qualiphy requests the caller offers beside the in-house GFE. When
   * given, they share one "New GFE" menu with "Start in-house GFE" instead of a
   * second button.
   */
  vendorRequests?: { label: string; onSelect: () => void }[];
  /**
   * The appointment's Request GFE step is already done (a vendor GFE covered it, or
   * staff ticked it). The in-house verdict then only describes in-house GFEs, and
   * starting another isn't the next step.
   */
  visitHandled?: boolean;
  /** The Request GFE step was ticked at booking, not by a person. */
  visitAutoCompleted?: boolean;
  /**
   * On an appointment: the SpaKinect / Qualiphy GFEs on file, shown with the in-house
   * ones under "GFEs on file" rather than as a box per source.
   */
  otherGfes?: ReactNode;
  otherGfesCount?: number;
}

const CLEARING = [GFEItemOutcome.APPROVED, GFEItemOutcome.CONDITIONAL];

const OUTCOME_BADGE: Record<
  GFEItemOutcome,
  { label: string; variant: "success" | "warning" | "error" | "neutral" }
> = {
  [GFEItemOutcome.APPROVED]: { label: "Approved", variant: "success" },
  [GFEItemOutcome.CONDITIONAL]: { label: "Conditional", variant: "warning" },
  [GFEItemOutcome.DEFERRED]: { label: "Deferred", variant: "error" },
  [GFEItemOutcome.PENDING]: { label: "Not marked", variant: "neutral" },
};

/** Newest first, by when the exam was signed or, failing that, started. */
function byNewest(a: PatientGFEDetail, b: PatientGFEDetail) {
  return (b.examDate ?? b.createdDate).localeCompare(
    a.examDate ?? a.createdDate,
  );
}

/**
 * Where one treatment the visit needs stands, from the patient's in-house GFEs.
 * ``covered`` comes from the server, which judges validity at the visit's start.
 */
function treatmentStatus(
  treatmentId: string,
  covered: boolean,
  inHouse: PatientGFEDetail[],
) {
  const itemOf = (gfe: PatientGFEDetail) =>
    gfe.items.find((item) => item.ref === treatmentId);
  const newest = [...inHouse].sort(byNewest);
  if (covered) {
    const gfe = newest.find((g) => {
      const outcome = itemOf(g)?.outcome;
      return isValidClearance(g) && !!outcome && CLEARING.includes(outcome);
    });
    const item = gfe && itemOf(gfe);
    return { kind: "covered" as const, gfe, item };
  }
  const inProgress = newest.find(
    (g) => g.examStatus === GFEExamStatus.IN_PROGRESS && itemOf(g),
  );
  if (inProgress) {
    return {
      kind: "in-progress" as const,
      gfe: inProgress,
      item: itemOf(inProgress),
    };
  }
  const deferredBy = newest.find(
    (g) =>
      (g.examStatus === GFEExamStatus.COMPLETED ||
        g.examStatus === GFEExamStatus.REJECTED) &&
      itemOf(g)?.outcome === GFEItemOutcome.DEFERRED,
  );
  if (deferredBy) {
    return {
      kind: "deferred" as const,
      gfe: deferredBy,
      item: itemOf(deferredBy),
    };
  }
  return { kind: "needed" as const, gfe: undefined, item: undefined };
}

function NoteLink({ noteId }: { noteId: string | null }) {
  const { tenant } = useTenant();
  if (!noteId || !tenant?.subDomain) return null;
  return (
    <Link
      href={`/${tenant.subDomain}/scribe/playground/${noteId}`}
      className={cx("text-caption", clickableText)}
    >
      View note
    </Link>
  );
}

function listNames(names: string[]) {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * The patient's in-house Good Faith Exams and the Start GFE button, which opens
 * a note from the clinic's GFE template. Signing that note completes the exam.
 */
export function InHouseGFESection({
  patientId,
  patient,
  eventId,
  locationId,
  showHeading = false,
  className,
  vendorRequests = [],
  visitHandled = false,
  visitAutoCompleted = false,
  otherGfes,
  otherGfesCount = 0,
}: InHouseGFESectionProps) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [showOnFile, setShowOnFile] = useState(false);
  const { tenant } = useTenant();
  const { currentUser } = useUser();
  // Starting a GFE writes a note; the context endpoint needs the same permission.
  const canStart = hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_WRITE);
  const {
    data: gfes,
    isLoading,
    isError,
  } = useQuery(gfeQueryOptions.patientGfes(patientId));
  const { data: context, isError: contextError } = useQuery({
    ...gfeQueryOptions.inHouseContext(eventId),
    enabled: canStart,
  });
  // Anyone who can see the patient gets the verdict, not just those who can start a GFE.
  const {
    data: visitCoverage,
    isLoading: visitCoverageLoading,
    isError: visitCoverageError,
  } = useQuery({
    ...gfeQueryOptions.inHouseVisitCoverage(eventId ?? "", patientId),
    enabled: !!eventId,
  });
  const { data: treatments, isLoading: treatmentsLoading } = useQuery({
    ...gfeQueryOptions.treatments(true),
    enabled: !!eventId,
  });
  const inHouse = (gfes ?? []).filter(
    (gfe) => gfe.source === GFESource.IN_HOUSE,
  );
  const templateIds = context?.templateIds ?? [];

  // On an appointment, judge the GFEs against what this visit needs: a valid GFE
  // that clears something else must not read as handled. The server judges it at
  // the visit's start, as the Request GFE tick does, so a GFE that lapses before
  // the visit doesn't read as covering it.
  const needed = visitCoverage?.treatmentIds ?? [];
  const cleared = new Set(visitCoverage?.coveredTreatmentIds ?? []);
  const missing = needed.filter((id) => !cleared.has(id));
  const nameOf = (id: string) =>
    treatments?.find((t) => t.id === id)?.name ?? "a treatment";
  const visitCovered = needed.length > 0 && missing.length === 0;
  // With what's needed already cleared, another GFE is the exception, not the next step.
  const startIsSecondary =
    visitHandled ||
    (needed.length > 0 ? visitCovered : inHouse.some(isValidClearance));
  const canStartInHouse = canStart && templateIds.length > 0;

  const templateHint = !canStart ? null : contextError ? (
    <p className="flex items-center gap-1 text-caption text-status-error-text">
      <FiAlertCircle className="h-3 w-3" />
      Couldn&apos;t load GFE templates. Refresh to try again.
    </p>
  ) : context && templateIds.length === 0 ? (
    <p className="text-caption text-muted-foreground">
      To start in-house GFEs, add a GFE clearance field to a{" "}
      <Link
        href={`/${tenant?.subDomain ?? ""}/settings/note-templates`}
        className={clickableText}
      >
        note template
      </Link>
      .
    </p>
  ) : null;

  const newGfeControl =
    vendorRequests.length > 0 ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={startIsSecondary ? "secondary" : "primary"}
            size="sm"
            className="w-fit"
          >
            New GFE
            <FiChevronDown className="ml-1 h-3.5 w-3.5" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canStartInHouse && (
            <DropdownMenuItem onClick={() => setNoteOpen(true)}>
              Start in-house GFE
            </DropdownMenuItem>
          )}
          {vendorRequests.map((request) => (
            <DropdownMenuItem key={request.label} onClick={request.onSelect}>
              {request.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : (
      canStartInHouse && (
        <Button
          variant={startIsSecondary ? "secondary" : "primary"}
          size="sm"
          className="w-fit"
          onClick={() => setNoteOpen(true)}
        >
          {startIsSecondary ? "Start new GFE" : "Start GFE"}
        </Button>
      )
    );

  const noteDialog = noteOpen && (
    // Straight to a manual note on the GFE template: a GFE is filled in by hand.
    <CreateNoteWrapper
      open
      onOpenChange={setNoteOpen}
      initialNoteType={NoteType.MANUAL}
      patient={patient}
      overrideLocationId={locationId}
      defaultNoteTemplateId={context?.defaultTemplateId ?? null}
      suggestedNoteTemplateIds={templateIds}
      eventId={eventId ?? null}
    />
  );

  if (eventId) {
    // On an appointment: the answer to "can we treat today?" first, then each
    // treatment the visit needs, then every GFE on file folded away.
    const loading =
      isLoading ||
      visitCoverageLoading ||
      (needed.length > 0 && treatmentsLoading);
    const statuses = needed.map((id) => ({
      id,
      name: nameOf(id),
      ...treatmentStatus(id, cleared.has(id), inHouse),
    }));
    const coveringGfes = Array.from(
      new Map(
        statuses.flatMap((s) =>
          s.kind === "covered" && s.gfe ? [[s.gfe.id, s.gfe] as const] : [],
        ),
      ).values(),
    );
    const waitingOn = statuses.find((s) => s.kind === "in-progress")?.gfe;
    const missingNames = statuses
      .filter((s) => s.kind !== "covered")
      .map((s) => s.name);
    // Another source covers the visit; the in-house rows would only contradict it.
    const showTreatments = needed.length > 0 && (visitCovered || !visitHandled);
    const onFileCount = inHouse.length + otherGfesCount;

    let verdict: {
      tone: "success" | "warning" | "info";
      title: string;
      detail: ReactNode;
    };
    if (visitCovered) {
      const only = coveringGfes.length === 1 ? coveringGfes[0] : undefined;
      verdict = {
        tone: "success",
        title: "Covered for this visit",
        detail: only ? (
          <>
            {[
              "In-house GFE",
              only.examiningProviderName && `by ${only.examiningProviderName}`,
              only.validUntil && `valid until ${formatDate(only.validUntil)}`,
            ]
              .filter(Boolean)
              .join(" · ")}{" "}
            <NoteLink noteId={only.noteId} />
          </>
        ) : (
          "In-house GFEs on file clear every treatment."
        ),
      };
    } else if (visitHandled) {
      verdict = {
        tone: "success",
        title: "Covered for this visit",
        detail: visitAutoCompleted
          ? "A GFE on file covered it when the visit was booked."
          : "Marked done by staff.",
      };
    } else if (waitingOn) {
      verdict = {
        tone: "info",
        title: waitingOnLabel(waitingOn) ?? "In-house GFE in progress",
        detail: (
          <>
            {`Started ${formatDate(waitingOn.createdDate)}. Covers nothing until it's complete.`}{" "}
            <NoteLink noteId={waitingOn.noteId} />
          </>
        ),
      };
    } else if (needed.length > 0) {
      verdict = {
        tone: "warning",
        title: `${listNames(missingNames)} ${missingNames.length === 1 ? "isn't" : "aren't"} covered`,
        detail: `No GFE on file clears ${missingNames.length === 1 ? "it" : "them"} for this visit.`,
      };
    } else {
      verdict = {
        tone: "warning",
        title: "Not covered yet",
        detail: "No GFE on file covers this visit.",
      };
    }
    const VerdictIcon =
      verdict.tone === "success"
        ? FiCheck
        : verdict.tone === "info"
          ? FiClock
          : FiAlertTriangle;
    const toneText = {
      success: "text-status-success-text",
      warning: "text-status-warning-text",
      info: "text-status-info-text",
    }[verdict.tone];

    return (
      <div className={cx("flex flex-col gap-2", className)}>
        {loading ? (
          <Skeleton className="h-12 w-full rounded-md" />
        ) : isError || visitCoverageError ? (
          <p className="flex items-center gap-1 text-caption text-status-error-text">
            <FiAlertCircle className="h-3 w-3" />
            Couldn&apos;t check this visit&apos;s GFEs. Refresh to try again.
          </p>
        ) : (
          <>
            <div className="flex items-start gap-2">
              <VerdictIcon
                className={cx("mt-0.5 h-4 w-4 shrink-0", toneText)}
                aria-hidden
              />
              <div className="flex min-w-0 flex-col gap-0.5">
                <p className={cx("text-body-strong", toneText)}>
                  {verdict.title}
                </p>
                <p className="text-caption text-foreground-secondary">
                  {verdict.detail}
                </p>
              </div>
            </div>
            {showTreatments && (
              <ul className="flex flex-col gap-1.5 border-t border-border pt-2">
                {statuses.map((s) => {
                  const badge =
                    s.kind === "covered" && s.item
                      ? OUTCOME_BADGE[s.item.outcome]
                      : s.kind === "deferred"
                        ? OUTCOME_BADGE[GFEItemOutcome.DEFERRED]
                        : s.kind === "in-progress"
                          ? {
                              label: "In progress",
                              variant: "neutral" as const,
                            }
                          : { label: "Needs GFE", variant: "warning" as const };
                  return (
                    <li key={s.id} className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-2">
                        <span className="min-w-0 grow truncate text-caption text-foreground">
                          {s.name}
                        </span>
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                      </div>
                      {s.item?.conditionNote &&
                        (s.kind === "covered" || s.kind === "deferred") && (
                          <p className="text-caption text-foreground-secondary">
                            {s.item.conditionNote}
                          </p>
                        )}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
        {templateHint}
        <div className="flex flex-wrap items-center justify-between gap-2">
          {onFileCount > 0 ? (
            <button
              type="button"
              aria-expanded={showOnFile}
              onClick={() => setShowOnFile((open) => !open)}
              className={cx(
                "flex items-center gap-1 text-caption",
                clickableText,
              )}
            >
              {showOnFile ? (
                <FiChevronDown className="h-3 w-3" aria-hidden />
              ) : (
                <FiChevronRight className="h-3 w-3" aria-hidden />
              )}
              {`${onFileCount} ${onFileCount === 1 ? "GFE" : "GFEs"} on file`}
            </button>
          ) : (
            <span />
          )}
          {newGfeControl}
        </div>
        {showOnFile && onFileCount > 0 && (
          <div className="flex flex-col gap-1">
            {inHouse.length > 0 && (
              <div className="flex flex-col">
                <p className="text-tiny uppercase tracking-wide text-muted-foreground">
                  In-house
                </p>
                <ul className="divide-y divide-border">
                  {inHouse.map((gfe) => (
                    <InHouseGFERow key={gfe.id} gfe={gfe} />
                  ))}
                </ul>
              </div>
            )}
            {otherGfes}
          </div>
        )}
        {noteDialog}
      </div>
    );
  }

  return (
    <div className={cx("flex flex-col gap-1", className)}>
      {showHeading && (
        <p className="text-tiny uppercase tracking-wide text-muted-foreground">
          In-house
        </p>
      )}
      {isLoading ? (
        <Skeleton className="h-10 w-full rounded-md" />
      ) : isError ? (
        <p className="flex items-center gap-1 text-caption text-status-error-text">
          <FiAlertCircle className="h-3 w-3" />
          Couldn&apos;t load in-house GFEs. Refresh to try again.
        </p>
      ) : inHouse.length > 0 ? (
        <ul className="divide-y divide-border">
          {inHouse.map((gfe) => (
            <InHouseGFERow key={gfe.id} gfe={gfe} />
          ))}
        </ul>
      ) : (
        <p className="flex items-center gap-1 text-caption text-muted-foreground">
          <FiClock className="h-3 w-3" />
          No in-house GFE on file.
        </p>
      )}
      {!isLoading && !isError && visitCoverageError ? (
        <p className="flex items-center gap-1 text-caption text-status-error-text">
          <FiAlertCircle className="h-3 w-3" />
          Couldn&apos;t check this visit&apos;s coverage. Refresh to try again.
        </p>
      ) : !isLoading &&
        !isError &&
        needed.length > 0 &&
        // Wait for the names, so the line never reads "a treatment, a treatment".
        !treatmentsLoading ? (
        <p
          className={cx(
            "text-caption",
            visitCovered
              ? "text-status-success-text"
              : visitHandled
                ? "text-foreground-muted"
                : "text-status-warning-text",
          )}
        >
          {visitCovered
            ? "Covers this visit"
            : // Another source already covers the visit, so this only speaks for in-house GFEs.
              `${visitHandled ? "In-house GFEs don't cover" : "Doesn't cover"}: ${missing.map(nameOf).join(", ")}`}
        </p>
      ) : null}
      {templateHint}
      {newGfeControl}
      {noteDialog}
    </div>
  );
}
